/* eslint-disable sonarjs/no-duplicate-string */
import {
  Button,
  FileInput,
  Image,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Stack,
  TextInput,
  toast,
} from 'component-library';
import { ChangeEvent, FormEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  PLOT_MEDIA_FILE_ALLOWED_FORMATS,
  PLOT_MEDIA_FILE_TYPE,
  PlotOverviewMediaFile,
  PlotOverviewMediaFileType,
} from '@/api/rest/resources/types/plot';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components';
import { FRONTEND_MAX_UPLOAD_FILE_SIZE_MB } from '@/config/constants';
import { useFileUpload } from '@/hooks/useFileUpload';
import { useCreatePlotOverviewMediaFile, useUpdatePlotOverviewMediaFile } from '@/hooks/usePlotOverviewMediaFile';
import { Logger } from '@/lib/logs/logger';
import { isNullish } from '@/utils/booleans';

import { PlotOverviewMediaFileFormFields, usePlotOverviewMediaFileForm } from '../hooks/useMediaFileControlsForm';

const REQUIRED_FIELD_ERROR_MESSAGE = 'global.ui.form.input.required';
const FILE_UPLOAD_ERROR_MESSAGE = 'shared.mediaUpload.plotOverview.form.toast.mediaFile.error.fileUpload';

export const MediaFileForm = ({
  mediaFile,
  plotOverviewId,
  onClose,
}: {
  mediaFile?: PlotOverviewMediaFile;
  plotOverviewId: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation();

  const { submit: submitPatchMediaFile, isSubmitting: isPatchSubmitting } = useUpdatePlotOverviewMediaFile();
  const { submit: submitCreateMediaFile, isSubmitting: isCreateSubmitting } = useCreatePlotOverviewMediaFile();
  const { submit: submitFileUpload, isSubmitting: isFileUploadSubmitting } = useFileUpload();

  const form = usePlotOverviewMediaFileForm({
    defaultValues: {
      [PlotOverviewMediaFileFormFields.name]: mediaFile?.name ?? '',
      [PlotOverviewMediaFileFormFields.caption]: mediaFile?.caption ?? '',
      [PlotOverviewMediaFileFormFields.type]: mediaFile?.type ?? undefined,
    },
  });

  const isCreateForm = isNullish(mediaFile?.id);

  const [formMediaType, selectedFile, selectedThumbnail] = form.watch([
    PlotOverviewMediaFileFormFields.type,
    PlotOverviewMediaFileFormFields.file,
    PlotOverviewMediaFileFormFields.thumbnail,
  ]) as [PlotOverviewMediaFileType, File | undefined, File | undefined];

  const allowedFileFormats = PLOT_MEDIA_FILE_ALLOWED_FORMATS[`${formMediaType}`] ?? [];

  const thumbnailPreview = useMemo(() => {
    if (selectedThumbnail) return URL.createObjectURL(selectedThumbnail);
    if (mediaFile?.thumbnail_url) return mediaFile.thumbnail_url;
    return null;
  }, [mediaFile?.thumbnail_url, selectedThumbnail]);

  const handleSave = form.handleSubmit(async () => {
    if (!mediaFile?.id) {
      return;
    }

    const formValues = form.getValues();
    const data: {
      name: string;
      caption: string;
      thumbnail_file_info_id?: string;
    } = {
      name: formValues[PlotOverviewMediaFileFormFields.name],
      caption: formValues[PlotOverviewMediaFileFormFields.caption],
    };

    const thumbnail = formValues[PlotOverviewMediaFileFormFields.thumbnail];

    if (formMediaType === PLOT_MEDIA_FILE_TYPE.AUDIO && thumbnail) {
      const uploadedThumbnail = await submitFileUpload(thumbnail, undefined, () =>
        toast({
          title: t(FILE_UPLOAD_ERROR_MESSAGE),
          type: 'error',
        }),
      );

      data.thumbnail_file_info_id = uploadedThumbnail?.file_info;
    }

    await submitPatchMediaFile(
      {
        id: mediaFile.id,
        ...data,
      },
      () =>
        toast({
          title: t('shared.mediaUpload.plotOverview.form.toast.mediaFile.success.update'),
          type: 'success',
        }),
      () =>
        toast({
          title: t('shared.mediaUpload.plotOverview.form.toast.mediaFile.error.update'),
          type: 'error',
        }),
    );

    onClose();
  });

  const handleSelectFile = (
    e: ChangeEvent<HTMLInputElement> | FormEvent<HTMLButtonElement>,
    field: PlotOverviewMediaFileFormFields,
  ) => {
    const file = (e.target as HTMLInputElement).files?.[0];

    if (!file) {
      return;
    }

    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > FRONTEND_MAX_UPLOAD_FILE_SIZE_MB) {
      form.setValue(field, undefined, { shouldDirty: true });
      form.setError(field, {
        type: 'manual',
        message: t('global.ui.form.input.maxFileSize', { maxSize: FRONTEND_MAX_UPLOAD_FILE_SIZE_MB }),
      });
      return;
    }

    form.setValue(field, file, { shouldDirty: true, shouldValidate: true });
  };

  const handleCreate = form.handleSubmit(async (formValues) => {
    const data = {
      name: formValues[PlotOverviewMediaFileFormFields.name],
      caption: formValues[PlotOverviewMediaFileFormFields.caption],
      mediaFile: formValues[PlotOverviewMediaFileFormFields.file],
      thumbnail: formValues[PlotOverviewMediaFileFormFields.thumbnail],
      type: formValues[PlotOverviewMediaFileFormFields.type],
    };

    const fileType = data.mediaFile?.name?.split('.')?.pop() ?? '';

    const mediaFileSizeInMB = (data.mediaFile?.size ?? 0) / (1024 * 1024);
    if (data.mediaFile && mediaFileSizeInMB > FRONTEND_MAX_UPLOAD_FILE_SIZE_MB) {
      form.setError(PlotOverviewMediaFileFormFields.file, {
        type: 'manual',
        message: t('global.ui.form.input.maxFileSize', { maxSize: FRONTEND_MAX_UPLOAD_FILE_SIZE_MB }),
      });
      return;
    }

    const thumbnailSizeInMB = (data.thumbnail?.size ?? 0) / (1024 * 1024);
    if (data.thumbnail && thumbnailSizeInMB > FRONTEND_MAX_UPLOAD_FILE_SIZE_MB) {
      form.setError(PlotOverviewMediaFileFormFields.thumbnail, {
        type: 'manual',
        message: t('global.ui.form.input.maxFileSize', { maxSize: FRONTEND_MAX_UPLOAD_FILE_SIZE_MB }),
      });
      return;
    }

    if (!allowedFileFormats.includes(fileType)) {
      form.setError(PlotOverviewMediaFileFormFields.file, {
        type: 'manual',
        message: t('global.ui.form.input.invalidFileExtension'),
      });
      return;
    }

    if (!data.mediaFile) {
      form.setError(PlotOverviewMediaFileFormFields.file, {
        type: 'manual',
        message: t(REQUIRED_FIELD_ERROR_MESSAGE),
      });
      return;
    }

    const uploadedFile = await submitFileUpload(data.mediaFile, undefined, () =>
      toast({
        title: t(FILE_UPLOAD_ERROR_MESSAGE),
        type: 'error',
      }),
    );

    if (!uploadedFile?.file_info) {
      Logger.error(`Error while uploading media file ${data.mediaFile}`);
      toast({
        title: t(FILE_UPLOAD_ERROR_MESSAGE),
        type: 'error',
      });
      return;
    }

    let uploadedThumbnail = null;

    if (data.thumbnail) {
      uploadedThumbnail = await submitFileUpload(data.thumbnail, undefined, () =>
        toast({
          title: t(FILE_UPLOAD_ERROR_MESSAGE),
          type: 'error',
        }),
      );
    }

    const mediaFileData = {
      name: data.name,
      caption: data.caption,
      type: data.type as PlotOverviewMediaFileType,
      fileInfoId: uploadedFile.file_info,
      thumbnailFileInfoId: uploadedThumbnail?.file_info,
      plotOverviewId,
    };

    await submitCreateMediaFile(
      mediaFileData,
      () => {
        toast({
          title: t('shared.mediaUpload.plotOverview.form.toast.mediaFile.success.create'),
          type: 'success',
        });
        onClose();
      },
      () =>
        toast({
          title: t('shared.mediaUpload.plotOverview.form.toast.mediaFile.error.create'),
          type: 'error',
        }),
    );
  });

  return (
    <Form {...form}>
      <Stack spacing={6} className='w-full'>
        <FormField
          control={form.control}
          name={PlotOverviewMediaFileFormFields.type}
          rules={{ required: t(REQUIRED_FIELD_ERROR_MESSAGE) }}
          render={({ field: { value, ...field } }) => (
            <FormItem className='flex flex-col gap-1'>
              <FormLabel>{t('shared.mediaUpload.plotOverview.form.fields.type.label')} *</FormLabel>
              <FormControl>
                {isCreateForm ? (
                  <Select
                    value={value}
                    onValueChange={(newValue) => {
                      form.setValue(
                        PlotOverviewMediaFileFormFields.type,
                        newValue as unknown as PlotOverviewMediaFileType,
                        {
                          shouldDirty: true,
                          shouldValidate: true,
                        },
                      );
                      form.resetField(PlotOverviewMediaFileFormFields.file, { keepDirty: true });
                      form.resetField(PlotOverviewMediaFileFormFields.thumbnail, { keepDirty: true });
                    }}
                  >
                    <SelectTrigger>
                      {value
                        ? t(`shared.mediaUpload.plotOverview.labels.media.${value}`)
                        : t('shared.mediaUpload.plotOverview.form.fields.type.placeholder')}
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(PLOT_MEDIA_FILE_TYPE).map((type) => (
                        <SelectItem key={type} value={type}>
                          {t(`shared.mediaUpload.plotOverview.labels.media.${type}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <TextInput
                    placeholder={t('shared.mediaUpload.plotOverview.form.fields.type.placeholder')}
                    value={t(
                      `shared.mediaUpload.plotOverview.labels.media.${mediaFile?.type as unknown as PlotOverviewMediaFileType}`,
                    )}
                    disabled={true}
                    {...field}
                  />
                )}
              </FormControl>
              <FormMessage>{form.formState.errors[PlotOverviewMediaFileFormFields.type]?.message}</FormMessage>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={PlotOverviewMediaFileFormFields.name}
          rules={{ required: t(REQUIRED_FIELD_ERROR_MESSAGE) }}
          render={({ field: { value, ...field } }) => (
            <FormItem className='flex flex-col gap-1'>
              <FormLabel>{t('shared.mediaUpload.plotOverview.form.fields.name.label')} *</FormLabel>
              <FormControl>
                <TextInput
                  placeholder={t('shared.mediaUpload.plotOverview.form.fields.name.placeholder')}
                  value={value}
                  {...field}
                />
              </FormControl>
              <FormMessage>{form.formState.errors[PlotOverviewMediaFileFormFields.name]?.message}</FormMessage>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={PlotOverviewMediaFileFormFields.caption}
          render={({ field: { value, ...field } }) => (
            <FormItem className='flex flex-col gap-1'>
              <FormLabel>{t('shared.mediaUpload.plotOverview.form.fields.caption.label')}</FormLabel>
              <FormControl>
                <TextInput
                  placeholder={t('shared.mediaUpload.plotOverview.form.fields.caption.placeholder')}
                  value={value}
                  {...field}
                />
              </FormControl>
            </FormItem>
          )}
        />
        {isCreateForm && (
          <FormField
            control={form.control}
            name={PlotOverviewMediaFileFormFields.file}
            rules={{ required: t(REQUIRED_FIELD_ERROR_MESSAGE) }}
            render={() => (
              <FormItem className='flex flex-col gap-1'>
                <FormLabel>{t('shared.mediaUpload.plotOverview.form.fields.file.label')} *</FormLabel>
                <FormControl>
                  <FileInput
                    disabled={isNullish(formMediaType)}
                    accept={allowedFileFormats.map((format) => `.${format}`).join(',')}
                    helperText={
                      allowedFileFormats.length > 0
                        ? `${t('shared.mediaUpload.plotOverview.form.labels.maxFileSize', {
                            maxSize: FRONTEND_MAX_UPLOAD_FILE_SIZE_MB,
                          })}. ${t('global.misc.allowedFiles', {
                            types: allowedFileFormats.join(', '),
                          })}`
                        : t('shared.mediaUpload.plotOverview.form.labels.maxFileSize', {
                            maxSize: FRONTEND_MAX_UPLOAD_FILE_SIZE_MB,
                          })
                    }
                    onChange={(e) => handleSelectFile(e, PlotOverviewMediaFileFormFields.file)}
                    fileName={selectedFile?.name}
                  />
                </FormControl>
                <FormMessage>{form.formState.errors[PlotOverviewMediaFileFormFields.file]?.message}</FormMessage>
              </FormItem>
            )}
          />
        )}
        {formMediaType === PLOT_MEDIA_FILE_TYPE.AUDIO && (
          <FormField
            control={form.control}
            name={PlotOverviewMediaFileFormFields.thumbnail}
            render={() => (
              <FormItem className='flex flex-col gap-1'>
                <FormLabel>{t('shared.mediaUpload.plotOverview.form.fields.thumbnail.label')}</FormLabel>
                <FormControl>
                  <Stack direction='row' spacing={10} className='items-center'>
                    <FileInput
                      disabled={isNullish(formMediaType)}
                      accept={PLOT_MEDIA_FILE_ALLOWED_FORMATS[PLOT_MEDIA_FILE_TYPE.IMAGE]
                        .map((format) => `.${format}`)
                        .join(',')}
                      helperText={`${t('shared.mediaUpload.plotOverview.form.labels.maxFileSize', {
                        maxSize: FRONTEND_MAX_UPLOAD_FILE_SIZE_MB,
                      })}. ${t('global.misc.allowedFiles', {
                        types: PLOT_MEDIA_FILE_ALLOWED_FORMATS[PLOT_MEDIA_FILE_TYPE.IMAGE].join(', '),
                      })}`}
                      onChange={(e) => handleSelectFile(e, PlotOverviewMediaFileFormFields.thumbnail)}
                      fileName={selectedThumbnail?.name}
                    />
                    {thumbnailPreview && (
                      <Image
                        className='h-20 w-auto object-cover mb-2 rounded'
                        src={thumbnailPreview}
                        alt={t('shared.mediaUpload.plotOverview.form.fields.thumbnail.label')}
                      />
                    )}
                  </Stack>
                </FormControl>
                <FormMessage>{form.formState.errors[PlotOverviewMediaFileFormFields.thumbnail]?.message}</FormMessage>
              </FormItem>
            )}
          />
        )}
        {isCreateForm ? (
          <Stack direction='row' className='w-full justify-end'>
            <Button type='submit' onClick={handleCreate} loading={isCreateSubmitting || isFileUploadSubmitting}>
              {t('shared.mediaUpload.plotOverview.form.button.create')}
            </Button>
          </Stack>
        ) : (
          <Stack direction='row' className='w-full justify-end'>
            <Button type='submit' onClick={handleSave} loading={isPatchSubmitting || isFileUploadSubmitting}>
              {t('shared.mediaUpload.plotOverview.form.button.save')}
            </Button>
          </Stack>
        )}
      </Stack>
    </Form>
  );
};
